import React from "react";

import type { ISession } from "@mne-authentication/api";

import { type Palette, type PaletteColor } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { createArgusTheme } from "@mnemonic/mui";
import { CoreMuiProvider } from "@mnemonic/mui";
import type { ICoreMuiProviderProps } from "@mnemonic/mui";
import { createMnemonicDarkTheme, createMnemonicLightTheme } from "@mnemonic/mui/styles";
import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";

interface ICustomPalette extends Palette {
  tertiary: Array<PaletteColor>;
  criticality?: { low: PaletteColor; medium: PaletteColor; high: PaletteColor; critical: PaletteColor };
  getTertiaryColor: (index: number) => PaletteColor;
  getCriticalityColor: (
    criticality: number | "low" | "medium" | "high" | "critical" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL",
  ) => PaletteColor;
}

//This is necessary to override the default theme with our custom values and make the the theme available without needing to type it everywhere.
declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Theme {
    palette: ICustomPalette;
  }
}

interface IThemePreferences {
  themeModeFromLocalStorage?: "dark" | "light" | "auto" | string;
  browserPref: boolean;
}

const determineThemeWithoutSession = ({ themeModeFromLocalStorage, browserPref }: IThemePreferences): boolean => {
  const { metaInfo } = ArgusUtils.getTargetMeta() || {};

  if (metaInfo?.theme === "nfcert") return false;

  if (!themeModeFromLocalStorage || themeModeFromLocalStorage === "auto") {
    return browserPref;
  }

  return themeModeFromLocalStorage === "dark";
};

export interface IThemeProviderProps extends Omit<ICoreMuiProviderProps, "theme"> {
  children: React.ReactNode;
  session: ISession;
}
export const ThemeProvider = ({ children, domain, locale, session }: IThemeProviderProps) => {
  const browserPref = useMediaQuery("(prefers-color-scheme: dark)");
  const themeModeFromLocalStorage = localStorage?.getItem("ui.themeMode");
  const shouldShowDarkTheme = determineThemeWithoutSession({ browserPref, themeModeFromLocalStorage });

  return (
    <CoreMuiProvider
      domain={domain}
      locale={locale}
      theme={createArgusTheme(session, (createTheme, localizationOpts) => {
        /* Pass a empty object if there are no local overrides */
        const themeOptions = {};
        /* If we have a session we use the createArgusTheme to determine the theme*/
        if (session) {
          return createTheme(themeOptions, localizationOpts);
        }
        /* If session is not defined we use shouldShowDarkTheme to deterine the theme */
        if (shouldShowDarkTheme) {
          return createMnemonicDarkTheme(themeOptions, localizationOpts);
        }
        return createMnemonicLightTheme(themeOptions, localizationOpts);
      })}
    >
      {children}
    </CoreMuiProvider>
  );
};
